<template>
  <DetailCard :loading="loading" :error="errorMessage">
    <template #heading>
      <span class="pi pi-file-arrow-up"></span>
      <a :href="documentsExternalUrl" target="_blank">
        {{ $t(`messages.detail.documents.title`) }}
      </a>
    </template>
    <div>
      <!-- Maatükk: documents merge from every ehitis on the parcel. The chips
           make the sources visible and toggleable; parcel-crossing linear
           infrastructure (fiber lines, street lighting — hundreds of parcels,
           hundreds of documents) starts deselected. -->
      <div v-if="sources.length > 1" class="mt-3 flex flex-wrap gap-1.5 px-4">
        <button
          v-for="source in sources"
          :key="source.ehrCode"
          type="button"
          class="source-chip rte-tt"
          :class="{ 'is-active': selectedSources.has(source.ehrCode) }"
          :aria-pressed="selectedSources.has(source.ehrCode)"
          :aria-label="source.name"
          :data-tt="source.name + (source.parcelWide ? ' — ' + $t('messages.detail.documents.parcelWide') : '')"
          @click="toggleSource(source.ehrCode)"
        >
          <span
            class="chip-icon pi"
            :class="source.parcelWide ? 'pi-arrow-right-arrow-left' : source.type === 'Hoone' ? 'pi-home' : 'pi-box'"
          ></span>
          <span class="chip-name">{{ source.name }}</span>
          <span class="chip-count">{{ source.count }}</span>
        </button>
      </div>

      <Tabs v-model:value="selectedTab" class="mt-2" :dt="{
          tabpanel: {
            padding: 'p-0',
          },
      }">
        <TabList>
          <Tab value="filtered">Kehtivad</Tab>
          <Tab value="all">Kõik</Tab>
        </TabList>
      </Tabs>

      <DataTable
        striped-rows
        row-hover
        size="small"
        data-key="dokuId"
        :value="visibleDocuments"
        selection-mode="multiple"
        v-model:selection="selectedDocuments"
        @row-select="saveSelectedDocuments"
        @row-unselect="saveSelectedDocuments"
        @row-select-all="saveSelectedDocuments"
        @row-unselect-all="saveSelectedDocuments"
      >
        <Column selection-mode="multiple" header-style="width: 3rem"></Column>
        <Column v-for="field in fields" :key="field" :field="field" :header="field">
          <template #body="slotProps">
            <span v-if="field === 'Nr'" :class="fieldClasses.Nr">
              <a
                :href="`${slotProps.data.Url}`"
                target="_blank"
                v-if="slotProps.data.Url"
              >
                {{ slotProps.data[field] }}
              </a>
              <span v-else>{{ slotProps.data[field] }}</span>
            </span>
            <span
              v-else
              :class="fieldClasses[field]"
              v-tooltip.top="field === 'Nimetus' ? wrapTooltip(slotProps.data.Nimetus, 28) : wrapTooltip('')"
            >
              {{ slotProps.data[field] }}
            </span>
          </template>
        </Column>
        <Column
          v-if="documentContentEnabled"
          field="Sisu"
          :header="$t('messages.detail.documents.sisu')"
        >
          <template #body="slotProps">
            <Skeleton
              v-if="slotProps.data.SisuStaatus === 'pending'"
              width="10rem"
              height="1rem"
            />
            <!-- Structured render (RED-294 follow-up): the flat sentence is
                 activity + work items + scope, so show the short activity
                 fully with count pills at fixed row height; the work list
                 rides on hover. Rows without structured columns (free-text
                 or legacy) fall back to the truncated sentence. -->
            <span
              v-else-if="slotProps.data.SisuTegevus"
              class="flex min-w-0 items-center gap-1.5 text-sm"
              v-tooltip.top="sisuTooltip(slotProps.data)"
            >
              <span class="truncate">{{ sisuActivity(slotProps.data.SisuTegevus) }}</span>
              <span v-if="slotProps.data.SisuTapsustused?.length" class="sisu-pill">
                {{ $t('messages.detail.documents.tood', slotProps.data.SisuTapsustused.length) }}
              </span>
              <span v-if="slotProps.data.SisuOsad" class="sisu-pill">
                {{ slotProps.data.SisuOsad }}
              </span>
            </span>
            <span
              v-else
              class="block max-w-[18rem] truncate text-sm"
              v-tooltip.top="wrapTooltip(slotProps.data.Sisu, 30)"
            >
              {{ slotProps.data.Sisu }}
            </span>
          </template>
        </Column>
        <Column
          v-if="sources.length > 1"
          field="sourceName"
          :header="$t('messages.detail.documents.source')"
        >
          <template #body="slotProps">
            <span
              class="block max-w-48 truncate text-sm text-surface-500"
              v-tooltip.top="wrapTooltip(slotProps.data.sourceName, 20)"
            >
              {{ slotProps.data.sourceName }}
            </span>
          </template>
        </Column>
      </DataTable>
    </div>
  </DetailCard>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, readonly, ref, shallowRef, watch } from 'vue'
import DetailCard from '@/components/detail/DetailCard.vue'
import { useFeature } from '@/composables/useFeature'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'

const store = useDetailStore()
const evaluationStore = useEvaluationStore()
const { documents, selectedDocuments } = storeToRefs(store)

const props = defineProps({
  ehrCode: { type: String, default: '' },
  // Maatükk mode: no main building — fetch and merge the documents of every
  // ehitis on the parcel (the codes come from the Hooned ja rajatised list).
  ehrCodes: { type: Array, default: () => [] },
  savedSelectedDocumentIds: { type: Array, default: () => [] },
})

const selectedTab = ref('filtered')

const loading = shallowRef(false)
const error = ref(false)
const errorMessage = shallowRef('')
const fields = readonly(['Nr', 'Kuupäev', 'Nimetus', 'Seisund'])

// Fixed-width columns stay compact so Sisu gets the flexible remainder.
const fieldClasses = readonly({
  Nr: 'font-mono text-xs whitespace-nowrap',
  Kuupäev: 'text-sm tabular-nums whitespace-nowrap',
  Nimetus: 'block max-w-[16rem] truncate text-sm',
  Seisund: 'text-xs text-surface-500 whitespace-nowrap',
})

// Every tooltip in this table wraps inside a bounded card — long content
// must never run off-screen as one nowrap strip. Empty value ⇒ no tooltip.
const TOOLTIP_WRAP_PT = {
  text: 'max-w-[20rem] whitespace-pre-line! break-normal! text-xs leading-relaxed',
}

const wrapTooltip = (value, minChars = 0) => ({
  value: value && String(value).length > minChars ? String(value) : '',
  pt: TOOLTIP_WRAP_PT,
})

// "Ehitise ümberehitamine" → "Ümberehitamine": the "Ehitise" prefix is
// redundant inside a building card. Display-only — export keeps the full
// sentence.
const sisuActivity = tegevus => {
  const stripped = tegevus.replace(/^Ehitise\s+/i, '')
  return stripped.charAt(0).toUpperCase() + stripped.slice(1)
}

// The work list is the only part not visible in the cell — hover reveals it.
const sisuTooltip = data => ({
  value: data.SisuTapsustused?.length
    ? [`${data.SisuTegevus}:`, ...data.SisuTapsustused.map(item => `• ${item}`)].join('\n')
    : '',
  pt: TOOLTIP_WRAP_PT,
})

// An ehitis whose kataster list spans more than this many parcels is linear
// infrastructure passing through (fiber trunk, street lighting, district
// heating) — its municipal document history is noise here by default.
const PARCEL_WIDE_THRESHOLD = 3

// Register bookkeeping, not documents a valuer cites.
const ADS_NOISE_LIIK = 'Aadressiandmete muutmiskanne ADS-i infosüsteemist'

// RED-294: "Dokumendi sisu" column, filled asynchronously by the backend.
// While a row's SisuStaatus is 'pending' the cell shows a Skeleton and the
// component polls the endpoint until the queued fetch lands (bounded — the
// backend marks every row fetched/unavailable/failed, so pending drains).
const documentContentEnabled = useFeature('ehr-document-content')

const CONTENT_POLL_INTERVAL_MS = 5000
const CONTENT_POLL_MAX_ATTEMPTS = 12

let contentPollTimer = null
let contentPollAttempts = 0

const hasPendingContent = () =>
  (documents.value?.everything || []).some(d => d.SisuStaatus === 'pending')

const scheduleContentPoll = () => {
  if (
    !documentContentEnabled.value ||
    contentPollTimer ||
    contentPollAttempts >= CONTENT_POLL_MAX_ATTEMPTS ||
    !hasPendingContent()
  ) {
    return
  }

  contentPollTimer = setTimeout(async () => {
    contentPollTimer = null
    contentPollAttempts++
    // Remap the selection onto the fresh row objects so a later export
    // payload carries the newly arrived Sisu values.
    const selectedIds = selectedDocuments.value.map(d => d.dokuId)
    await loadDocuments(effectiveEhrCodes.value)
    if (selectedIds.length) {
      restoreSelectedDocuments(selectedIds, documents.value)
    }
    scheduleContentPoll()
  }, CONTENT_POLL_INTERVAL_MS)
}

onUnmounted(() => {
  if (contentPollTimer) clearTimeout(contentPollTimer)
})

const documentsExternalUrl = computed(() => {
  if (props.ehrCode) {
    return 'https://ehr.ee/app/link/b/' + props.ehrCode + '/doc'
  }
  return '#'
})

const saveSelectedDocuments = () => {
  evaluationStore.save({
    assessment_data: {
      selected_documents: selectedDocuments.value.map(d => d.dokuId),
    },
  })
}

const restoreSelectedDocuments = (savedIds, allDocs) => {
  if (!savedIds?.length || !allDocs) return
  const allDocsList = [...(allDocs.filtered || []), ...(allDocs.everything || [])]
  const uniqueDocs = new Map(allDocsList.map(d => [d.dokuId, d]))
  selectedDocuments.value = savedIds
    .map(id => uniqueDocs.get(id))
    .filter(Boolean)
}

const effectiveEhrCodes = computed(() =>
  props.ehrCode ? [props.ehrCode] : props.ehrCodes.filter(Boolean),
)

// ehrCode → { name, type, parcelWide } from the Hooned ja rajatised rows.
const buildingsByCode = computed(() => {
  const map = new Map()
  for (const building of store.buildings || []) {
    if (!building.ehrCode) continue
    const parcelCount = building.cadaster ? building.cadaster.split(',').length : 1
    map.set(building.ehrCode, {
      name: building.name || building.ehrCode,
      type: building.type || '',
      parcelWide: parcelCount > PARCEL_WIDE_THRESHOLD,
    })
  }
  return map
})

const selectedSources = ref(new Set())

const sources = computed(() => {
  if (effectiveEhrCodes.value.length < 2) return []
  const docs = documents.value?.everything || []
  return effectiveEhrCodes.value.map(code => {
    const meta = buildingsByCode.value.get(code)
    return {
      ehrCode: code,
      name: meta?.name ?? code,
      type: meta?.type ?? '',
      parcelWide: meta?.parcelWide ?? false,
      count: docs.filter(d => d.ehrKood === code).length,
    }
  })
})

const toggleSource = code => {
  const next = new Set(selectedSources.value)
  if (next.has(code)) {
    next.delete(code)
  } else {
    next.add(code)
  }
  selectedSources.value = next
}

const parseEstonianDate = value => {
  // 'dd.mm.yyyy' → sortable timestamp; '-'/missing sorts last.
  const parts = String(value || '').split('.')
  if (parts.length !== 3) return 0
  return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0
}

const loadDocuments = async ehrCodes => {
  if (!ehrCodes.length) return

  loading.value = true
  try {
    const responses = await Promise.all(
      ehrCodes.map(code =>
        axios.get('/xroad/documents/' + code).then(r => ({ code, data: r.data })),
      ),
    )
    // Merge per-building document lists tagged with their source ehitis,
    // deduped by dokuId, newest first.
    const merged = { filtered: [], everything: [] }
    for (const key of ['filtered', 'everything']) {
      const byId = new Map()
      responses
        .flatMap(({ code, data }) =>
          (data?.[key] || []).map(doc => ({
            ...doc,
            ehrKood: doc.ehrKood ?? code,
            sourceName: buildingsByCode.value.get(code)?.name ?? code,
          })),
        )
        .forEach(doc => byId.set(doc.dokuId, doc))
      merged[key] = [...byId.values()].sort(
        (a, b) => parseEstonianDate(b['Kuupäev']) - parseEstonianDate(a['Kuupäev']),
      )
    }
    documents.value = merged

    // Default source selection: parcel-local ehitised on, crossing infra off.
    // (All on when everything on the parcel is crossing infra.)
    const local = ehrCodes.filter(code => !buildingsByCode.value.get(code)?.parcelWide)
    selectedSources.value = new Set(local.length ? local : ehrCodes)
  } catch (err) {
    error.value = true
    errorMessage.value = err
  } finally {
    loading.value = false
  }
}

watch(
  effectiveEhrCodes,
  async newVal => {
    if (newVal.length) {
      await loadDocuments(newVal)
      if (props.savedSelectedDocumentIds?.length) {
        restoreSelectedDocuments(props.savedSelectedDocumentIds, documents.value)
      }
      contentPollAttempts = 0
      scheduleContentPoll()
    }
  },
  { immediate: true },
)

const visibleDocuments = computed(() => {
  let docs =
    selectedTab.value === 'filtered'
      ? store.documents.filtered || []
      : store.documents.everything || []

  if (sources.value.length > 1) {
    docs = docs.filter(d => selectedSources.value.has(d.ehrKood))
  }

  // The ADS bookkeeping entries drown real documents on the default tab;
  // they stay reachable under "Kõik".
  if (selectedTab.value === 'filtered') {
    docs = docs.filter(d => d.Nimetus !== ADS_NOISE_LIIK)
  }

  return docs
})
</script>

<style scoped>
  @reference '../../../../css/tailwind.css';

  .source-chip {
    @apply inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-surface-200 bg-white px-2.5 text-xs font-medium text-surface-400 transition-all duration-150 active:scale-95 dark:border-surface-600 dark:bg-surface-900 dark:text-surface-500;

    &:hover {
      @apply border-surface-300 text-surface-600 shadow-xs dark:border-surface-500 dark:text-surface-300;
    }

    &:focus-visible {
      @apply outline-hidden ring-2 ring-primary-500/40;
    }

    &.is-active {
      @apply border-primary-300 bg-primary-50 text-primary-800 shadow-xs dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-200;

      .chip-count {
        @apply bg-primary-100 text-primary-700 dark:bg-primary-800/60 dark:text-primary-200;
      }

      .chip-icon {
        @apply text-primary-500 dark:text-primary-300;
      }
    }
  }

  .chip-icon {
    @apply text-[11px] text-surface-400 transition-colors dark:text-surface-500;
  }

  .chip-name {
    @apply max-w-44 truncate;
  }

  .chip-count {
    @apply min-w-5 rounded-full bg-surface-100 px-1.5 text-center text-[10px] leading-4 text-surface-500 transition-colors dark:bg-surface-700 dark:text-surface-300;
  }

  /* Non-interactive sibling of .chip-count for the Sisu cell pills —
     bordered and a step darker than .chip-count so they read at a glance. */
  .sisu-pill {
    @apply inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-surface-200 bg-surface-100 px-2 text-[11px] font-medium leading-5 text-surface-600 dark:border-surface-600 dark:bg-surface-800 dark:text-surface-300;
  }
</style>
